import {
  CdkDragDrop,
  moveItemInArray,
  transferArrayItem,
  CdkDropList,
} from '@angular/cdk/drag-drop';
import { Component, OnInit, QueryList, ViewChildren } from '@angular/core';
import { DataserviceService } from '../dataservice.service';
import { firstValueFrom } from 'rxjs';
import { NzMessageService } from 'ng-zorro-antd/message';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

@Component({
  selector: 'app-marketing-board',
  templateUrl: './marketing-board.component.html',
  styleUrl: './marketing-board.component.css',
})
export class MarketingBoardComponent implements OnInit {
  @ViewChildren('cardList') cardDropLists!: QueryList<CdkDropList>;
  currentUser: any;
  columns: any[] = [];
  unassignedColumn: any = null;
  bucketColumns: any[] = [];
  columnIds: string[] = [];
  connectedLists: CdkDropList[] = [];
  isCreateMarketModalVisible = false;
  createMarketForm: FormGroup;
  providers: any[] = [];
  isEditCardModalVisible = false;
  editCardForm: FormGroup;

  constructor(
    private data: DataserviceService,
    private message: NzMessageService,
    private fb: FormBuilder
  ) {
    this.currentUser = JSON.parse(localStorage.getItem('user')!);
    this.createMarketForm = this.fb.group({
      name: ['', Validators.required],
      provider: ['', Validators.required],
      leadCount: [0, [Validators.required, Validators.min(0)]],
      penetration: [0, [Validators.required, Validators.min(0)]],
      offer: ['', Validators.required],
      competition: ['', Validators.required],
      active: [false],
    });
    this.editCardForm = this.fb.group({
      id: ['', Validators.required],
      name: ['', Validators.required],
      provider: ['', Validators.required],
      leadCount: [0, [Validators.required, Validators.min(0)]],
      penetration: [0, [Validators.required, Validators.min(0)]],
      offer: ['', Validators.required],
      competition: ['', Validators.required],
      active: [false],
    });
  }

  ngOnInit() {
    this.getBoardByCompany(this.currentUser.company_id);
    this.loadProviders();
  }

  ngAfterViewChecked() {
    if (
      this.cardDropLists?.length &&
      this.cardDropLists.length !== this.connectedLists.length
    ) {
      setTimeout(() => {
        this.connectedLists = this.cardDropLists.toArray();
      }, 0);
    }
  }

  async getBoardByCompany(companyId: string) {
    const res = await firstValueFrom(
      this.data.getData(`market-mgmt/company/${companyId}/board`)
    );
    if (res?.result === 'success' && res?.data?.columns) {
      this.columns = res.data.columns;
      this.unassignedColumn =
        this.columns.find((c: any) => c.id === 'unassigned') ??
        this.columns[0] ??
        null;
      this.bucketColumns = this.columns.filter((c: any) => c.id !== 'unassigned');
      this.columnIds = this.columns.map((c: any) => c.id);
      setTimeout(() => {
        this.connectedLists = this.cardDropLists
          ? this.cardDropLists.toArray()
          : [];
      }, 0);
    } else {
      this.columns = [];
      this.unassignedColumn = null;
      this.bucketColumns = [];
      this.columnIds = [];
      this.connectedLists = [];
    }
  }

  /** Reorder bucket columns. Persists via backend. */
  async dropColumn(event: CdkDragDrop<any[]>) {
    if (event.previousIndex === event.currentIndex) return;
    const moved = this.bucketColumns[event.previousIndex];
    if (!moved?.id) return;
    moveItemInArray(this.bucketColumns, event.previousIndex, event.currentIndex);
    const placeAfterId =
      event.currentIndex === 0
        ? 'start'
        : this.bucketColumns[event.currentIndex - 1].id;
    try {
      const payload = { move_to_after_id: placeAfterId };
      await firstValueFrom(
        this.data.postData(payload, `market-mgmt/buckets/update/${moved.id}`)
      );
      this.message.success('Columns reordered');
    } catch (e) {
      moveItemInArray(this.bucketColumns, event.currentIndex, event.previousIndex);
      this.message.error('Failed to reorder columns');
    }
  }

  async drop(event: CdkDragDrop<any[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
      return;
    }
    const card = event.previousContainer.data[event.previousIndex];
    const listIndex = this.connectedLists.indexOf(event.container);
    const allColumns = [this.unassignedColumn, ...this.bucketColumns].filter(
      Boolean
    );
    const targetColumn =
      listIndex >= 0 && listIndex < allColumns.length
        ? allColumns[listIndex]
        : null;
    const newBucketId = targetColumn?.id ?? event.container.id;
    if (!newBucketId) return;

    transferArrayItem(
      event.previousContainer.data,
      event.container.data,
      event.previousIndex,
      event.currentIndex
    );

    try {
      const bucketName = targetColumn?.title ?? 'bucket';
      if (card?.isUnassigned || card?.assignmentId == null) {
        await firstValueFrom(
          this.data.postData(
            {
              market_id: card.marketId,
              company_id: this.currentUser.company_id,
              bucket_id: newBucketId,
              moved_by: this.currentUser?.id,
            },
            'market-mgmt/assign-market-to-bucket'
          )
        );
        this.message.success(`Market assigned to ${bucketName}`);
      } else {
        await firstValueFrom(
          this.data.postData(
            { bucket_id: newBucketId, moved_by: this.currentUser?.id },
            `market-mgmt/assignments/${card.assignmentId}/move`
          )
        );
        this.message.success(`Market moved to ${bucketName}`);
      }
    } catch (e) {
      this.message.error('Assign/move failed');
      transferArrayItem(
        event.container.data,
        event.previousContainer.data,
        event.currentIndex,
        event.previousIndex
      );
    }
  }

  loadProviders() {
    const userId = this.currentUser?.id;
    if (!userId) return;
    this.data.getData(`providers/providers-by-user/${userId}`).subscribe(
      (response: any) => {
        this.providers = response?.data ?? (Array.isArray(response) ? response : []);
      },
      () => this.message.error('Failed to load providers')
    );
  }

  openCreateMarketModal() {
    this.createMarketForm.reset({
      name: '',
      provider: '',
      leadCount: 0,
      penetration: 0,
      offer: '',
      competition: '',
      active: false,
    });
    this.isCreateMarketModalVisible = true;
  }

  closeCreateMarketModal() {
    this.isCreateMarketModalVisible = false;
    this.createMarketForm.reset();
  }

  async submitCreateMarket() {
    if (this.createMarketForm.invalid) {
      Object.values(this.createMarketForm.controls).forEach((c) => c.markAsTouched());
      return;
    }
    const value = this.createMarketForm.value;
    const payload = {
      ...value,
      leadCount: value.leadCount ?? 0,
      penetration: value.penetration ?? 0,
      offer: value.offer ?? '',
      competition: value.competition ?? '',
      active: value.active ?? false,
      company_id: this.currentUser.company_id,
    };
    try {
      const res = await firstValueFrom(this.data.postData(payload, 'market-mgmt'));
      if (res?.id || res?.data?.id) {
        this.message.success('Market created');
        this.closeCreateMarketModal();
        this.getBoardByCompany(this.currentUser.company_id);
      } else {
        this.message.error(res?.message || 'Failed to create market');
      }
    } catch (e) {
      this.message.error('Failed to create market');
    }
  }

  editCard(card: any) {
    this.isEditCardModalVisible = true;
    console.log('editCard', card);
    this.editCardForm.patchValue({
      id: card.id,
      name: card.name,
      provider: card.provider,
      leadCount: card.leadCount,
      penetration: card.penetration,
      offer: card.offer,
      competition: card.competition,
      active: card.active,
    });
  }

  closeEditCardModal() {
    this.isEditCardModalVisible = false;
    this.editCardForm.reset();
  }

  async submitEditCard() {
    if (this.editCardForm.invalid) {
      Object.values(this.editCardForm.controls).forEach((c) => c.markAsTouched());
      return;
    }
    const value = this.editCardForm.value;
    if (!value.id) {
      this.message.error('Market ID is required');
      return;
    }
    const payload = {
      ...value,
      leadCount: value.leadCount ?? 0,
      penetration: value.penetration ?? 0,
      offer: value.offer ?? '',
      competition: value.competition ?? '',
      active: value.active ?? false,
    };
    try {
      const res = await firstValueFrom(this.data.postData(payload, `market-mgmt/market-update/${this.editCardForm.value.id}`));
      if (res?.result === 'success') {
        this.message.success('Market updated');
        this.closeEditCardModal();
        this.getBoardByCompany(this.currentUser.company_id);
      } else {
        this.message.error(res?.message || 'Failed to update market');
      }
    } catch (e) {
      this.message.error('Failed to update market');
    }
  }

  async deleteCard(card: any) {
    const payload = {
      ...card,
      leadCount: card.leadCount ?? 0,
      penetration: card.penetration ?? 0,
      offer: card.offer ?? '',
      competition: card.competition ?? '',
    };
    try {
      const res = await firstValueFrom(this.data.postData(payload, `market-mgmt/market-delete/${card.id}`));
      if (res?.result === 'success') {
        this.message.success('Market deleted');
        this.closeEditCardModal();
        this.getBoardByCompany(this.currentUser.company_id);
      } else {
        this.message.error(res?.message || 'Failed to delete market');
      }
    } catch (e) {
      this.message.error('Failed to delete market');
    }
  }
}